import {FormArray, FormGroup, ValidationErrors, ValidatorFn} from '@angular/forms';
import {EmploymentStatus} from '../../../shared/enums/employment-status';
import {AssistanceApplicationFileType} from '../../../shared/enums/assistance-application-file-type';
import {AssistanceApplicationType, Client} from '../../../services/api.client';

function countFileType(fileType: AssistanceApplicationFileType, filesArray: FormArray): number {
    return filesArray.controls.filter(control => control.get('fileType').value === fileType).length;
}

function isStormApplication(assistanceRequest: any): boolean {
    return assistanceRequest.stormAppliances ||
           assistanceRequest.stormConstruction ||
           assistanceRequest.stormHvacPlumbing ||
           assistanceRequest.stormMoldRemediationAssistance ||
           assistanceRequest.stormOtherDisasterCausedUnmetNeeds ||
           assistanceRequest.stormTemporaryStorageAssistance;
}

export function requiredDocumentsUploadedValidator(client: Client, applicationType?: AssistanceApplicationType): ValidatorFn {
    return (form: FormGroup): ValidationErrors | null => {

        /* skip validation for denied apps ----------------------------------*/
        if (form.get('status').value === 'Denied') { return null; }

        /* ------------------------------------------------------------------
         * helpers & raw data
         * ---------------------------------------------------------------- */
        const assistanceRequest = (form.get('assistanceApplicationAreasOfNeed') as FormGroup).value;
        const applicantAdult = {
            firstName: client?.firstName,
            lastName: client?.lastName,
            totalMonthlyIncome: +client?.individualMonthlyIncome,
            employmentStatus: client?.employmentStatus
        };
        const additionalAdults = (form.get('adults') as FormArray).value as {
            employmentStatus: EmploymentStatus;
        }[];
        const filesArray = form.get('files') as FormArray;
        const allAdults   = [applicantAdult, ...additionalAdults];
        const count       = (t: AssistanceApplicationFileType) => countFileType(t, filesArray);

        const errors: ValidationErrors = {};

        /* ------------------------------------------------------------------
         * core / always‑required docs (skip for storm applications)
         * ---------------------------------------------------------------- */
        const isStorm = isStormApplication(assistanceRequest);

        if (!isStorm && count(AssistanceApplicationFileType.PhotoId) < 1) {
            errors.photoIdRequired = true;
        }

        /* employment / residency equivalency --------------------------------*/
        const employmentEquivalents = [
            AssistanceApplicationFileType.UtilityBill,
            AssistanceApplicationFileType.TaxStatement,
            AssistanceApplicationFileType.TaxReturns,
            AssistanceApplicationFileType.BankStatement,
            AssistanceApplicationFileType.GovtOrCourtRecord,
            AssistanceApplicationFileType.GovtBenefitsStatement,
            AssistanceApplicationFileType.SsiSsdiAwardLetter,
            AssistanceApplicationFileType.UnemploymentAward,
            AssistanceApplicationFileType.WicSnapAwardLetter,
            AssistanceApplicationFileType.EmployerLetter
        ];
        if (!isStorm && employmentEquivalents.reduce((s, t) => s + count(t), 0) < 1) {
            errors.utilityOrEquivalentRequired = true;
        }

        /* residency proof (STLWater) ---------------------------------------*/
        const residencyEquivalents = [
            AssistanceApplicationFileType.ProofOfResidence,
            AssistanceApplicationFileType.PhotoId,
            AssistanceApplicationFileType.UtilityBill,
            AssistanceApplicationFileType.TaxReturns,
            AssistanceApplicationFileType.VoterRegistration
        ];
        if (assistanceRequest.stlWaterWater &&
            residencyEquivalents.reduce((s, t) => s + count(t), 0) < 1) {
            errors.residenceProofRequired = true;
        }

        /* income docs ------------------------------------------------------*/
        const incomeDocs = {
            checkStub:           count(AssistanceApplicationFileType.CheckStub),
            pensionLetter:       count(AssistanceApplicationFileType.PensionLetter),
            govtBenefits:        count(AssistanceApplicationFileType.GovtBenefitsStatement),
            ssiSsdi:             count(AssistanceApplicationFileType.SsiSsdiAwardLetter),
            unemployment:        count(AssistanceApplicationFileType.UnemploymentAward),
            wicSnap:             count(AssistanceApplicationFileType.WicSnapAwardLetter),
            taxReturns:          count(AssistanceApplicationFileType.TaxReturns),
            pls:                 count(AssistanceApplicationFileType.ProfitLossStatement),
            armedForces:         count(AssistanceApplicationFileType.ArmedForcesAllotment),
            tanf:                count(AssistanceApplicationFileType.TANF),
            childSupport:        count(AssistanceApplicationFileType.ChildSupport),
            unionFunds:          count(AssistanceApplicationFileType.UnionFunds)
        };

        const selfEmployed = allAdults.filter(a => a.employmentStatus === EmploymentStatus.SelfEmployed);
        const nonSelfEmp   = allAdults.filter(a => a.employmentStatus !== EmploymentStatus.SelfEmployed);

        if (!isStorm && selfEmployed.length &&
            (incomeDocs.taxReturns + incomeDocs.pls) < selfEmployed.length) {
            errors.incomeDocumentRequiredForSelfEmployed = true;
        }

        const nonSEIncomeCount = incomeDocs.checkStub + incomeDocs.pensionLetter + incomeDocs.govtBenefits +
            incomeDocs.ssiSsdi + incomeDocs.unemployment + incomeDocs.wicSnap +
            incomeDocs.armedForces + incomeDocs.tanf + incomeDocs.childSupport + incomeDocs.unionFunds;

        if (!isStorm && nonSelfEmp.length && nonSEIncomeCount < nonSelfEmp.length) {
            errors.incomeDocumentRequiredForNonSelfEmployed = true;
        }

        /* ------------------------------------------------------------------
         * area‑specific document checks
         * ---------------------------------------------------------------- */

        /* utility assistance */
        if (assistanceRequest.uhonsElectric || assistanceRequest.uhonsGasHeat ||
            assistanceRequest.uhonsWater    || assistanceRequest.uhonsSewer   ||
            assistanceRequest.uffUtilityBills) {
            if (count(AssistanceApplicationFileType.UtilityBill) +
                count(AssistanceApplicationFileType.UtilityShutoffNotice) < 1) {
                errors.utilityBillOrShutoffNoticeRequired = true;
            }
        }

        /* childcare */
        if (assistanceRequest.uhonsChildcareExpenses ||
            assistanceRequest.uffStateLicensedChildcareProvider) {
            if (count(AssistanceApplicationFileType.ChildcareBill) < 1) {
                errors.childcareBillRequired = true;
            }
        }

        /* car repair */
        if (assistanceRequest.uhonsCarRepair || assistanceRequest.uffCarRepair) {
            if (count(AssistanceApplicationFileType.AutoRepairBill) < 1) {
                errors.autoRepairBillRequired = true;
            }
        }

        /* rent (UHONS) */
        if (assistanceRequest.uhonsRent) {
            if (count(AssistanceApplicationFileType.LeaseAgreement) < 1) {
                errors.leaseAgreementRequired = true;
            }
            if (count(AssistanceApplicationFileType.DelinquentRentNotice) < 1) {
                errors.delinquentRentNoticeRequired = true;
            }
        }

        /* rent (UFF) */
        if (assistanceRequest.uffRentPayment) {
            if (count(AssistanceApplicationFileType.LeaseAgreement) < 1) {
                errors.leaseAgreementRequired = true;
            }
            if (count(AssistanceApplicationFileType.DelinquentRentNotice) < 1) {
                errors.delinquentRentNoticeRequired = true;
            }
            if (count(AssistanceApplicationFileType.LandlordProofOfOwnership) < 1) {
                errors.landlordOwnershipRequired = true;
            }
            if (count(AssistanceApplicationFileType.RentLandlordForm) < 1) {
                errors.rentLandlordFormRequired = true;
            }
        }

        /* security deposit (UFF) */
        if (assistanceRequest.uffSecurityDeposit) {
            if (count(AssistanceApplicationFileType.LeaseAgreement) < 1) {
                errors.leaseAgreementRequired = true;
            }
            if (count(AssistanceApplicationFileType.LandlordProofOfOwnership) < 1) {
                errors.landlordOwnershipRequired = true;
            }
        }

        /* eviction */
        if (assistanceRequest.evictionNotification) {
            if (count(AssistanceApplicationFileType.EvictionCourtOrder) +
                count(AssistanceApplicationFileType.EvictionNotice) < 1) {
                errors.evictionCourtOrderRequired = true;
            }
        }

        /* mortgage */
        if (assistanceRequest.uffMortgagePayment &&
            count(AssistanceApplicationFileType.MortgageStatement) < 1) {
            errors.mortgageStatementRequired = true;
        }

        /* moving expenses */
        if (assistanceRequest.uffMovingExpenses &&
            count(AssistanceApplicationFileType.MovingExpenseBill) < 1) {
            errors.movingExpenseBillRequired = true;
        }

        /* car insurance / payment */
        if (assistanceRequest.uffCarInsurance &&
            count(AssistanceApplicationFileType.AutoInsuranceBill) < 1) {
            errors.autoInsuranceBillRequired = true;
        }

        if (assistanceRequest.uffCarPayment &&
            count(AssistanceApplicationFileType.AutoPaymentStatement) < 1) {
            errors.autoPaymentStatementRequired = true;
        }

        /* car ownership (any car-related) */
        if ((assistanceRequest.uffCarInsurance ||
                assistanceRequest.uffCarPayment   ||
                assistanceRequest.uffCarRepair) &&
            count(AssistanceApplicationFileType.AutoTitle) < 1) {
            errors.autoTitleRequired = true;
        }

        /* tuition */
        if (assistanceRequest.uffTuition &&
            count(AssistanceApplicationFileType.TuitionBill) < 1) {
            errors.tuitionBillRequired = true;
        }

        /* educational supplies */
        if (assistanceRequest.uffChildEdSupportUniforms) {
            if (count(AssistanceApplicationFileType.ChildEducationBill) < 1) {
                errors.childEducationBillRequired = true;
            }
            if (count(AssistanceApplicationFileType.ChildEnrollmentProof) < 1) {
                errors.childEnrollmentProofRequired = true;
            }
            if (count(AssistanceApplicationFileType.ChildEducationProgressReport) < 1) {
                errors.childProgressReportRequired = true;
            }
        }

        /* tutoring */
        if (assistanceRequest.uffChildSupportTutoring) {
            if (count(AssistanceApplicationFileType.ChildTutoringBill) < 1) {
                errors.tutoringInvoiceRequired = true;
            }
            if (count(AssistanceApplicationFileType.ChildEnrollmentProof) < 1) {
                errors.childEnrollmentProofRequired = true;
            }
        }

        /* STL Water assistance */
        if (assistanceRequest.stlWaterWater) {
            if (count(AssistanceApplicationFileType.DelinquentWaterBill) < 1) {
                errors.delinquentWaterBillRequired = true;
            }
        }

        /* Storm assistance */

// 1) Appliances (Furniture / Goods / Appliances): Invoice OR Estimate
        if (assistanceRequest.stormAppliances) {
            if (count(AssistanceApplicationFileType.ApplianceInvoice) < 1) {
                errors.stormAppliancesInvoiceOrEstimateRequired = true;
            }
        }

// 2) Construction (general home repairs): Invoice OR Estimate
        if (assistanceRequest.stormConstruction) {
            if (count(AssistanceApplicationFileType.ConstructionBidInvoice) < 1) {
                errors.stormConstructionInvoiceOrEstimateRequired = true;
            }
        }


// 3) HVAC / Plumbing: Invoice OR Estimate (PDF “Other Utility”)
        if (assistanceRequest.stormHvacPlumbing) {
            if (count(AssistanceApplicationFileType.HvacPlumbingBidInvoice) < 1) {
                errors.stormHvacPlumbingInvoiceOrEstimateRequired = true;
            }
        }

// 4) Mold Remediation Assistance: Mold Remediation Invoice (licensed provider)
        if (assistanceRequest.stormMoldRemediationAssistance) {
            if (count(AssistanceApplicationFileType.MoldRemediationInvoice) < 1) {
                errors.stormMoldRemediationInvoiceRequired = true;
            }
        }

// 5) Other Disaster-Caused Unmet Needs: Invoice OR Estimate
        if (assistanceRequest.stormOtherDisasterCausedUnmetNeeds) {
            if (count(AssistanceApplicationFileType.OtherDisasterNeeds) < 1) {
                errors.stormOtherUnmetNeedsInvoiceOrEstimateRequired = true;
            }
        }

// 6) Temporary Storage Assistance: Storage Statement OR Estimate
        if (assistanceRequest.stormTemporaryStorageAssistance) {
            if (count(AssistanceApplicationFileType.StorageStatement) < 1) {
                errors.stormStorageStatementOrEstimateRequired = true;
            }
        }


        /* SSN proof – only required for application types that enable it */
        const ssnRequiredAppTypes = [AssistanceApplicationType.STLWater];
        const requiresSsn = ssnRequiredAppTypes.includes(applicationType);
        const ssnDocs = count(AssistanceApplicationFileType.SsnProof) +
            count(AssistanceApplicationFileType.TaxReturns) +
            count(AssistanceApplicationFileType.SsiSsdiAwardLetter);
        if (requiresSsn && ssnDocs < 1) { errors.ssnProofRequired = true; }

        /* ------------------------------------------------------------------ */
        return Object.keys(errors).length ? errors : null;
    };
}
